import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import LayoutHeader from '../../layout/LayoutHeader';
import { getDataAPI, putDataAPI } from '../../utils/fetchData';
import { getCachedData, refreshData } from '../../utils/dataPreloader';

const TicketCounter = () => {
    const navigate = useNavigate();
    const cachedBookings = useSelector((state) => state.dataCache?.bookings?.data);
    const [bookings, setBookings] = useState([]);
    const [ticketCounts, setTicketCounts] = useState({});
    const [lastCountDates, setLastCountDates] = useState({});
    const [isSaving, setIsSaving] = useState({});
    const [unsavedChanges, setUnsavedChanges] = useState(new Set());
    const [manualPercentages, setManualPercentages] = useState({}); // Track manually set percentages

    useEffect(() => {
        const processBookings = (bookingsData) => {
                // Filter to only upcoming shows
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const upcomingBookings = (Array.isArray(bookingsData) ? bookingsData : [])
                    .filter(booking => {
                        const perfDate = new Date(booking.perf_date);
                        perfDate.setHours(0, 0, 0, 0);
                        return perfDate >= today;
                    })
                    .sort((a, b) => new Date(a.perf_date) - new Date(b.perf_date));

                setBookings(upcomingBookings);

                // Initialize ticket counts and last count dates
                const counts = {};
                const dates = {};
                const percentages = {};
                upcomingBookings.forEach(booking => {
                    // Priority: current_ticket_count > ticket_count > tickets_sold
                    counts[booking._id] = booking.current_ticket_count || booking.ticket_count || booking.tickets_sold || booking.tix_sold || '';
                    if (booking.last_ticket_count_date) {
                        dates[booking._id] = booking.last_ticket_count_date;
                    }
                    // Initialize manual percentages if they exist in booking data
                    if (booking.percentage_sold !== undefined && booking.percentage_sold !== null) {
                        percentages[booking._id] = booking.percentage_sold;
                    }
                });
                setTicketCounts(counts);
                setLastCountDates(dates);
                setManualPercentages(percentages);
        };

        // Use cached data immediately if available
        if (cachedBookings && cachedBookings.length > 0) {
            processBookings(cachedBookings);
            }
        
        // Refresh in background if needed
        const token = JSON.parse(localStorage.getItem('dizeeToken'));
        if (token) {
            refreshData('bookings', 'user/getBookings', token).then((data) => {
                if (data && data.length > 0) {
                    processBookings(data);
        }
            });
        }
    }, [cachedBookings]);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            weekday: 'short', 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric' 
        });
    };

    const formatShortDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const month = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
        const day = date.getDate();
        return `${month} ${day}`;
    };

    const getDaysAgo = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        const today = new Date();
        const diffTime = Math.abs(today - date);
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const getTicketStatus = (booking, count) => {
        // Return 'unknown' if no count has been entered
        const sold = parseInt(count) || 0;
        if (sold === 0 || count === '' || !count) return 'unknown';
        
        // Need capacity to determine meaningful status
        if (!booking.capacity) return 'unknown';
        
        const capacity = parseInt(booking.capacity) || 0;
        if (capacity === 0) return 'unknown';
        
        const percentage = (sold / capacity) * 100;

        if (percentage >= 100) return 'sold-out';
        if (percentage >= 75) return 'selling-fast';
        if (percentage >= 50) return 'selling-fast';
        return 'slow';
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'sold-out':
                return 'Sold Out';
            case 'selling-fast':
                return 'Selling Fast';
            case 'slow':
                return 'Slow';
            default:
                return 'Unknown';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'sold-out':
                return 'bg-[#FF3B30] text-[#FF3B30]';
            case 'selling-fast':
                return 'bg-[#FF9500] text-[#FF9500]';
            case 'slow':
                return 'bg-[rgba(255,255,255,0.1)] text-[rgba(255,255,255,0.7)]';
            default:
                return 'bg-[rgba(255,255,255,0.1)] text-[rgba(255,255,255,0.7)]';
        }
    };

    const handleTicketCountChange = (bookingId, value) => {
        setTicketCounts(prev => ({
            ...prev,
            [bookingId]: value
        }));
        // Track unsaved changes
        setUnsavedChanges(prev => new Set(prev).add(bookingId));
        
        // Auto-calculate percentage if capacity exists and manual percentage not set
        const booking = bookings.find(b => b._id === bookingId);
        if (booking && !manualPercentages[bookingId]) {
            const capacity = parseInt(booking.capacity) || parseInt(booking.sellable_cap) || 0;
            const sold = parseInt(value) || 0;
            if (capacity > 0 && sold > 0) {
                // Auto-calculation happens in render, no need to set here
            }
        }
    };

    const handlePercentageChange = (bookingId, value) => {
        // Allow manual percentage override
        if (value === '' || value === null || value === undefined) {
            // Clear manual override, use auto-calculation
            setManualPercentages(prev => {
                const newState = { ...prev };
                delete newState[bookingId];
                return newState;
            });
        } else {
            setManualPercentages(prev => ({
                ...prev,
                [bookingId]: value
            }));
        }
        // Track unsaved changes
        setUnsavedChanges(prev => new Set(prev).add(bookingId));
    };

    const handleSaveAll = async () => {
        if (unsavedChanges.size === 0) {
            return;
        }
        await saveAllPending();
    };

    const saveTicketCount = async (bookingId, count) => {
        setIsSaving(prev => ({ ...prev, [bookingId]: true }));
        try {
            const token = JSON.parse(localStorage.getItem('dizeeToken'));
            const booking = bookings.find(b => b._id === bookingId);
            
            if (!booking) {
                console.error('Booking not found:', bookingId);
                return;
            }

            const numericCount = parseInt(count) || 0;
            const capacity = parseInt(booking.capacity) || parseInt(booking.sellable_cap) || 0;
            
            // Get percentage - use manual if set, otherwise auto-calculate
            const manualPercentage = manualPercentages[bookingId];
            const autoCalculatedPercentage = capacity > 0 && numericCount > 0 ? Math.round((numericCount / capacity) * 100) : 0;
            const percentageToSave = manualPercentage !== undefined && manualPercentage !== null && manualPercentage !== '' 
                ? parseFloat(manualPercentage) || 0 
                : autoCalculatedPercentage;
            
            const updateData = {
                ...booking, // Include existing booking data
                current_ticket_count: numericCount,
                ticket_count: numericCount,
                tickets_sold: numericCount,
                tix_sold: numericCount,
                percentage_sold: percentageToSave,
                last_ticket_count_date: new Date().toISOString()
            };

            console.log('Saving ticket count for booking:', bookingId, 'Count:', numericCount);
            const response = await putDataAPI('user/updateBooking', {
                bookingId: bookingId,
                data: updateData
            }, token);
            
            console.log('Save response:', response.data);
            
            // Update last count date immediately (before reload)
            const now = new Date().toISOString();
            setLastCountDates(prev => ({
                ...prev,
                [bookingId]: now
            }));

            // Update local state immediately
            setTicketCounts(prev => ({
                ...prev,
                [bookingId]: String(numericCount)
            }));

            // Update the booking object in state immediately
            setBookings(prev => prev.map(b => 
                b._id === bookingId 
                    ? { ...b, current_ticket_count: numericCount, ticket_count: numericCount, tickets_sold: numericCount, tix_sold: numericCount, last_ticket_count_date: now }
                    : b
            ));

            // Refresh bookings data in background to sync with server
            if (token) {
                refreshData('bookings', 'user/getBookings', token).then((data) => {
                    if (data && data.length > 0) {
                        const processBookings = (bookingsData) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const upcomingBookings = (Array.isArray(bookingsData) ? bookingsData : [])
                                .filter(booking => {
                                    const perfDate = new Date(booking.perf_date);
                                    perfDate.setHours(0, 0, 0, 0);
                                    return perfDate >= today;
                                })
                                .sort((a, b) => new Date(a.perf_date) - new Date(b.perf_date));
                            setBookings(upcomingBookings);
                        };
                        processBookings(data);
                    }
                });
            }
            
            // Remove from unsaved changes
            setUnsavedChanges(prev => {
                const newSet = new Set(prev);
                newSet.delete(bookingId);
                return newSet;
            });
        } catch (error) {
            console.error('Failed to save ticket count:', error);
            console.error('Error details:', error.response?.data || error.message);
            alert('Failed to save ticket count. Please try again.');
        } finally {
            setIsSaving(prev => ({ ...prev, [bookingId]: false }));
        }
    };

    const saveAllPending = async () => {
        if (unsavedChanges.size === 0) {
            console.log('No unsaved changes');
            return;
        }
        
        console.log('Saving all pending changes:', Array.from(unsavedChanges));
        
        const savePromises = Array.from(unsavedChanges).map(bookingId => {
            const count = ticketCounts[bookingId];
            if (count !== undefined && count !== '') {
                return saveTicketCount(bookingId, count);
            }
            return Promise.resolve();
        });
        
        try {
            await Promise.all(savePromises);
            console.log('All changes saved successfully');
        } catch (error) {
            console.error('Error saving all changes:', error);
            alert('Some changes failed to save. Please try again.');
        }
    };


    return (
        <LayoutHeader>
            <div className="flex justify-center w-full">
                <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
                    {/* Header */}
                    <div className="px-4 my-[50px] flex w-full justify-between items-center">
                        <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Ticket Counts</span>
                        <div className="flex items-center gap-4">
                            <span 
                                className={`text-[12px] font-normal cursor-pointer hover:opacity-70 ${
                                    unsavedChanges.size > 0 ? 'text-[#FFFF00]' : 'text-white'
                                }`}
                                onClick={handleSaveAll}
                            >
                                Save
                            </span>
                            <span 
                                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                                onClick={() => {
                                    const canGoBack = window.history.length > 1;
                                    if (canGoBack) {
                                        navigate(-1);
                                    } else {
                                        navigate('/bookings/view-all');
                                    }
                                }}
                            >
                                Go back
                            </span>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="w-full px-4 flex-1 overflow-y-auto">
                        {bookings.length === 0 ? (
                            <div className="text-left py-[60px]">
                                <h3 className="text-white text-[12px] font-normal mb-2">No upcoming shows found</h3>
                                <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px]">
                                    Add upcoming shows to track ticket counts.
                                </p>
                            </div>
                        ) : (
                            <div className="grid gap-4">
                                {bookings.map((booking) => {
                                    // Priority: current_ticket_count > ticket_count > tickets_sold > tix_sold
                                    const count = ticketCounts[booking._id] || booking.current_ticket_count || booking.ticket_count || booking.tickets_sold || booking.tix_sold || '';
                                    const status = getTicketStatus(booking, count);
                                    // Check both lastCountDates state and booking data for last count date
                                    const lastCountDate = lastCountDates[booking._id] || booking.last_ticket_count_date;
                                    const daysAgo = lastCountDate ? getDaysAgo(lastCountDate) : null;
                                    const hasCount = count !== '' && parseInt(count) > 0;
                                    const isSoldOut = status === 'sold-out';
                                    const capacity = parseInt(booking.capacity) || parseInt(booking.sellable_cap) || 0;
                                    const sold = parseInt(count) || 0;
                                    
                                    // Use manual percentage if set, otherwise auto-calculate
                                    const manualPercentage = manualPercentages[booking._id];
                                    const autoCalculatedPercentage = capacity > 0 && sold > 0 ? Math.round((sold / capacity) * 100) : 0;
                                    const percentage = manualPercentage !== undefined && manualPercentage !== null && manualPercentage !== '' 
                                        ? parseFloat(manualPercentage) || 0 
                                        : autoCalculatedPercentage;

                                    return (
                                        <div 
                                            key={booking._id}
                                            className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4"
                                        >
                                            <div className="flex items-start gap-4">
                                                {/* Date Section */}
                                                <div className="flex flex-col items-center min-w-[60px]">
                                                    <span className="text-[#FFFF00] text-[12px] font-normal">
                                                        {formatShortDate(booking.perf_date).split(' ')[0]}
                                                    </span>
                                                    <span className="text-[#FFFF00] text-[18px] font-normal">
                                                        {formatShortDate(booking.perf_date).split(' ')[1]}
                                                    </span>
                                                </div>

                                                {/* Main Content */}
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="text-white text-[12px] font-normal mb-1">
                                                        {booking.venue || booking.event_title || booking.artist_name || 'Unknown Venue'}
                                                    </h3>
                                                    <p className="text-[rgba(255,255,255,0.5)] text-[10px] mb-2">
                                                        {formatDate(booking.perf_date)}
                                                    </p>
                                                    
                                                    {/* Last Count, Capacity, and % Sold Info */}
                                                    <div className="flex flex-wrap items-center gap-2 mb-2">
                                                        {isSoldOut ? (
                                                            <span className="text-[rgba(255,255,255,0.7)] text-[10px]">
                                                                {percentage}% · Sold Out
                                                            </span>
                                                        ) : hasCount && daysAgo !== null ? (
                                                            <span className="text-[rgba(255,255,255,0.5)] text-[10px]">
                                                                Last Count: {daysAgo === 0 ? 'Today' : `${daysAgo} ${daysAgo === 1 ? 'day' : 'days'} ago`}
                                                            </span>
                                                        ) : hasCount && daysAgo === null ? (
                                                            <span className="text-[rgba(255,255,255,0.5)] text-[10px]">
                                                                Last Count: Today
                                                            </span>
                                                        ) : (
                                                            <span className="text-[rgba(255,255,255,0.5)] text-[10px]">
                                                                No count recorded
                                                            </span>
                                                        )}
                                                        
                                                        {/* Capacity Tag */}
                                                        {capacity > 0 && (
                                                            <span className="px-2 py-0.5 bg-[rgba(255,255,255,0.1)] border border-[rgba(255,255,255,0.2)] rounded text-[10px] text-[rgba(255,255,255,0.7)]">
                                                                Capacity: {capacity.toLocaleString()}
                                                            </span>
                                                        )}
                                                        
                                                        {/* % Sold - Editable */}
                                                        {capacity > 0 && (
                                                            <div className="flex items-center gap-1">
                                                                <input
                                                                    type="text"
                                                                    value={manualPercentages[booking._id] !== undefined && manualPercentages[booking._id] !== null && manualPercentages[booking._id] !== '' 
                                                                        ? manualPercentages[booking._id] 
                                                                        : (sold > 0 ? percentage : '')}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value.replace(/[^0-9.]/g, '');
                                                                        handlePercentageChange(booking._id, value);
                                                                    }}
                                                                    onBlur={(e) => {
                                                                        // Validate percentage on blur
                                                                        const value = parseFloat(e.target.value);
                                                                        if (isNaN(value) || value < 0) {
                                                                            handlePercentageChange(booking._id, '');
                                                                        } else if (value > 100) {
                                                                            handlePercentageChange(booking._id, '100');
                                                                        }
                                                                    }}
                                                                    placeholder={sold > 0 ? `${autoCalculatedPercentage}%` : '0%'}
                                                                    className="w-16 px-2 py-0.5 bg-[rgba(255,255,255,0.1)] border border-[rgba(255,255,255,0.2)] rounded text-[10px] text-[rgba(255,255,255,0.7)] focus:outline-none focus:border-[#FFFF00] text-center"
                                                                    disabled={isSaving[booking._id]}
                                                                />
                                                                <span className="text-[10px] text-[rgba(255,255,255,0.7)]">% Sold</span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Ticket Count Input */}
                                                    {!isSoldOut && (
                                                        <input
                                                            type="text"
                                                            value={count}
                                                            onChange={(e) => {
                                                                // Only update state, don't auto-save
                                                                handleTicketCountChange(booking._id, e.target.value);
                                                            }}
                                                            placeholder="Enter latest count..."
                                                            className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-[11px] placeholder-[rgba(255,255,255,0.5)] focus:outline-none focus:border-[#FFFF00] mb-2"
                                                            disabled={isSaving[booking._id]}
                                                        />
                                                    )}

                                                    {/* Status Badge - Only show if there's a meaningful status */}
                                                    {status !== 'unknown' && (
                                                        <div className="flex items-center gap-2">
                                                            <div className={`px-2 py-1 rounded-full ${getStatusColor(status)} text-[10px] font-normal flex items-center gap-1`}>
                                                                <div className={`w-2 h-2 rounded-full ${status === 'sold-out' ? 'bg-[#FF3B30]' : status === 'selling-fast' ? 'bg-[#FF9500]' : 'bg-[rgba(255,255,255,0.5)]'}`}></div>
                                                                {getStatusLabel(status)}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Right Side Info */}
                                                <div className="flex flex-col items-end min-w-[80px]">
                                                    <span className="text-[rgba(255,255,255,0.5)] text-[10px] mb-1">
                                                        Current
                                                    </span>
                                                    <span className={`text-[12px] font-normal ${isSoldOut ? 'text-[#FF3B30]' : 'text-[#FFFF00]'}`}>
                                                        {sold > 0 ? sold.toLocaleString() : '0'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        
                        {/* Save Button at Bottom */}
                        {bookings.length > 0 && (
                            <div className="mt-6 mb-4">
                                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                                    <button
                                        onClick={handleSaveAll}
                                        disabled={unsavedChanges.size === 0 || Object.values(isSaving).some(saving => saving)}
                                        className="w-full bg-[#FFFF00] text-black text-[12px] font-semibold py-3 rounded-lg hover:bg-[rgba(255,255,0,0.9)] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                    >
                                        {Object.values(isSaving).some(saving => saving) ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2"></div>
                                                Saving...
                                            </>
                                        ) : (
                                            `Save Ticket Counts${unsavedChanges.size > 0 ? ` (${unsavedChanges.size})` : ''}`
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </LayoutHeader>
    );
};

export default TicketCounter;

